import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Button,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    SelectChangeEvent,
    TextField,
    Slide,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { SiteUser } from "../../types";
import { useCreateSiteMutation, useGetTrialsUserManagementQuery, useGetSitesQuery } from "../../dataAccess";
import countriesList from "../../constants/Countries";
import theme from "../../../../app/theme";

interface AssignSitesModalProps {
    open: boolean;
    user: SiteUser | null;
    onClose: () => void;
    onSave: (
        userId: string,
        newSiteIds: string[],
        assignedSites: { uuid: string; siteName: string; trialName?: string; trialUuid?: string }[]
    ) => void;
}

const AssignSitesModal: React.FC<AssignSitesModalProps> = ({ open, user, onClose, onSave }) => {
    const [selectedTrial, setSelectedTrial] = useState<string>("");
    const [selectedSite, setSelectedSite] = useState<string>("");
    const [assignments, setAssignments] = useState<
        { siteName: string; uuid: string; trialName?: string; trialUuid?: string }[]
    >([]);
    const [createSiteDrawerOpen, setCreateSiteDrawerOpen] = useState<boolean>(false);
    const [newSite, setNewSite] = useState<{ siteName: string; country: string; trialId: string }>({
        siteName: "",
        country: "",
        trialId: "",
    });
    const [createdSites, setCreatedSites] = useState<{ name: string; uuid: string; trialId: string }[]>([]);
    const [createSite] = useCreateSiteMutation();
    const { data: availableTrials } = useGetTrialsUserManagementQuery();
    const {
        data: availableSites,
        isLoading: sitesLoading,
        isFetching: sitesFetching,
    } = useGetSitesQuery(selectedTrial, {
        skip: !selectedTrial,
    });
    const isSitesLoading = sitesLoading || sitesFetching;

    useEffect(() => {
        if (open && user) {
            const initialTrialId = user.trial?.uuid || availableTrials?.[0]?.uuid || "";
            const currentSites = user.sites || [];
            setAssignments(
                currentSites.map((site) => ({
                    siteName: site.siteName,
                    uuid: site.uuid,
                    trialName: site.trialName || site.trial?.trialName || user.trial?.trialName || "Unknown Trial",
                    trialUuid: site.trial?.uuid || user.trial?.uuid,
                }))
            );
            setSelectedTrial(initialTrialId);
            setSelectedSite("");
            setCreateSiteDrawerOpen(false);
            setNewSite({ siteName: "", country: "", trialId: initialTrialId });
            setCreatedSites([]);
        }
    }, [open, user, availableTrials]);

    if (!user) return null;

    const handleTrialChange = (event: SelectChangeEvent<string>) => {
        const trialId = event.target.value;
        setSelectedTrial(trialId);
        setSelectedSite("");
        setNewSite((prev) => ({ ...prev, trialId }));
    };

    const handleSiteChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;

        if (value === "__CREATE_NEW_SITE__") {
            if (!selectedTrial) return;
            setCreateSiteDrawerOpen(true);
            setNewSite((prev) => ({ ...prev, trialId: selectedTrial }));
            return;
        }

        setSelectedSite(value);
    };

    const handleAddSite = () => {
        if (!selectedSite || isSitesLoading) return;

        const mergedSites = [
            ...(availableSites || []),
            ...createdSites
                .filter((site) => site.trialId === selectedTrial)
                .map((site) => ({
                    name: site.name,
                    uuid: site.uuid,
                })),
        ];

        const site = mergedSites.find((s) => s.uuid === selectedSite);
        if (!site) return;

        const isAlreadyInAssignments = assignments.some((s) => s.uuid === site.uuid);

        if (!isAlreadyInAssignments) {
            const trialName =
                availableTrials?.find((trial) => trial.uuid === selectedTrial)?.name ||
                user.trial?.trialName ||
                "Unknown Trial";
            setAssignments([
                ...assignments,
                {
                    siteName: site.name,
                    uuid: site.uuid,
                    trialName,
                    trialUuid: selectedTrial,
                },
            ]);
        }
        setSelectedSite("");
    };

    const handleRemoveAssignment = (uuid: string) => {
        setAssignments(assignments.filter((s) => s.uuid !== uuid));
    };

    const handleSave = () => {
        const siteIds = assignments.map((s) => s.uuid);
        onSave(user.uuid, siteIds, assignments);
        onClose();
    };

    const handleCloseCreateSite = () => {
        setCreateSiteDrawerOpen(false);
        setNewSite({ siteName: "", country: "", trialId: selectedTrial || "" });
    };

    const handleNewSiteChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setNewSite((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleConfirmCreateSite = async () => {
        if (!newSite.siteName || !newSite.country || !newSite.trialId) return;

        const createdNewSite = await createSite({
            name: newSite.siteName,
            countryCode: newSite.country,
            trialId: newSite.trialId,
        }).unwrap();

        setCreatedSites((prev) => {
            if (prev.some((site) => site.uuid === createdNewSite.uuid)) {
                return prev;
            }
            return [...prev, { ...createdNewSite, trialId: newSite.trialId }];
        });
        setSelectedSite(createdNewSite.uuid);
        setCreateSiteDrawerOpen(false);
        setNewSite({ siteName: "", country: "", trialId: selectedTrial || "" });
    };

    const sitesForSelectedTrial = [
        ...(availableSites || []),
        ...createdSites
            .filter((site) => site.trialId === selectedTrial)
            .map((site) => ({
                name: site.name,
                uuid: site.uuid,
            })),
    ]
        .filter((site, index, self) => self.findIndex((candidate) => candidate.uuid === site.uuid) === index)
        .sort((a, b) => a.name.localeCompare(b.name));

    const availableSitesFiltered = sitesForSelectedTrial.filter(
        (site) => !assignments.some((a) => a.uuid === site.uuid)
    );
    const visibleSites = isSitesLoading ? [] : availableSitesFiltered;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { position: "relative", overflow: "hidden" } }}
        >
            <DialogTitle sx={{ bgcolor: theme.palette.background.paper, color: "#fff", pb: 1 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h6">Assign Sites to User</Typography>
                        <Typography variant="body2" color="textSecondary">
                            Add new site assignments for {user.email}
                        </Typography>
                    </Box>
                    <IconButton onClick={onClose} sx={{ color: "#fff" }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ bgcolor: theme.palette.background.paper, color: "#fff", pt: 20, pb: 3 }}>
                <Box mb={20}>
                    <Typography variant="subtitle2" sx={{ mb: 8, color: "#fff" }}>
                        Trial
                    </Typography>
                    <FormControl fullWidth size="small">
                        <InputLabel sx={{ color: "#aaa" }}>Trial</InputLabel>
                        <Select
                            value={selectedTrial}
                            onChange={handleTrialChange}
                            label="Trial"
                            sx={{
                                "bgcolor": theme.palette.background.paper,
                                "color": "#fff",
                                ".MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
                                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#666" },
                                ".MuiSvgIcon-root": { color: "#aaa" },
                            }}
                        >
                            {availableTrials?.map((trial) => (
                                <MenuItem key={trial.uuid} value={trial.uuid}>
                                    {trial.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
                <Box mb={20}>
                    <Typography variant="subtitle2" sx={{ mb: 8, color: "#fff" }}>
                        Site
                    </Typography>
                    <Box display="flex" gap={8}>
                        <FormControl fullWidth size="small">
                            <InputLabel sx={{ color: "#aaa" }}>
                                {!selectedTrial
                                    ? "Select a trial first"
                                    : isSitesLoading
                                    ? "Loading sites..."
                                    : availableSitesFiltered && availableSitesFiltered.length === 0
                                    ? "No sites available"
                                    : "Search sites..."}
                            </InputLabel>
                            <Select
                                value={selectedSite}
                                onChange={handleSiteChange}
                                label={
                                    !selectedTrial
                                        ? "Select a trial first"
                                        : isSitesLoading
                                        ? "Loading sites..."
                                        : availableSitesFiltered && availableSitesFiltered.length === 0
                                        ? "No sites available"
                                        : "Search sites..."
                                }
                                disabled={!selectedTrial || isSitesLoading}
                                sx={{
                                    "bgcolor": theme.palette.background.paper,
                                    "color": "#fff",
                                    ".MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
                                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#666" },
                                    ".MuiSvgIcon-root": { color: "#aaa" },
                                }}
                            >
                                {visibleSites?.map((site) => (
                                    <MenuItem key={site.uuid} value={site.uuid}>
                                        {site.name}
                                    </MenuItem>
                                ))}
                                {selectedTrial && !isSitesLoading && (
                                    <MenuItem
                                        value="__CREATE_NEW_SITE__"
                                        sx={{
                                            color: "#4caf50",
                                            bgcolor: theme.palette.background.paper,
                                            fontStyle: "italic",
                                        }}
                                    >
                                        Create site...
                                    </MenuItem>
                                )}
                            </Select>
                        </FormControl>
                        <Button
                            variant="contained"
                            sx={{
                                minWidth: "80px",
                            }}
                            onClick={handleAddSite}
                            disabled={!selectedSite || isSitesLoading}
                        >
                            Add
                        </Button>
                    </Box>
                </Box>
                <Box>
                    <Typography variant="subtitle2" sx={{ mb: 8, color: "#aaa" }}>
                        New Assignments
                    </Typography>
                    <Box sx={{ bgcolor: theme.palette.background.paper, borderRadius: 1, p: 10, minHeight: "60px" }}>
                        {assignments.length > 0 ? (
                            <Box display="flex" flexWrap="wrap" gap={8}>
                                {assignments.map((site) => (
                                    <Chip
                                        key={site.uuid}
                                        label={`${site.siteName} -> ${site.trialName || "Unknown Trial"}`}
                                        onDelete={() => handleRemoveAssignment(site.uuid)}
                                        sx={{
                                            "bgcolor": "rgba(76, 175, 80, 0.15)",
                                            "color": "#4caf50",
                                            "border": "1px solid #4caf50",
                                            ".MuiChip-deleteIcon": { color: "#4caf50" },
                                        }}
                                    />
                                ))}
                            </Box>
                        ) : (
                            <Typography variant="body2" sx={{ color: "#666" }}>
                                No sites assigned
                            </Typography>
                        )}
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ bgcolor: theme.palette.background.paper, py: 16, px: 24 }}>
                <Button onClick={onClose} sx={{ mr: 2 }}>
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleSave}>
                    Assign ({assignments.length})
                </Button>
            </DialogActions>
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    pointerEvents: createSiteDrawerOpen ? "auto" : "none",
                    zIndex: 10,
                }}
            >
                {createSiteDrawerOpen && (
                    <Box
                        onClick={handleCloseCreateSite}
                        sx={{
                            position: "absolute",
                            inset: 0,
                            bgcolor: "rgba(0,0,0,0.45)",
                            zIndex: 1,
                        }}
                    />
                )}
                <Slide direction="left" in={createSiteDrawerOpen} mountOnEnter unmountOnExit>
                    <Box
                        sx={{
                            width: { xs: 320, sm: 380 },
                            bgcolor: theme.palette.background.paper,
                            maxHeight: "90%",
                            height: "auto",
                            boxShadow: 8,
                            borderLeft: "1px solid #333",
                            display: "flex",
                            flexDirection: "column",
                            pointerEvents: "auto",
                            zIndex: 2,
                            overflowY: "auto",
                            borderRadius: 1,
                        }}
                    >
                        <Box sx={{ p: 16, borderBottom: "1px solid #333" }}>
                            <Typography variant="h6" sx={{ color: "#fff" }}>
                                Create new site
                            </Typography>
                            <Typography variant="body2" sx={{ color: "#aaa", mt: 4 }}>
                                Add a site to the selected trial
                            </Typography>
                        </Box>
                        <Box sx={{ p: 16, display: "flex", flexDirection: "column", gap: 12 }}>
                            <TextField
                                label="Site Name"
                                name="siteName"
                                value={newSite.siteName}
                                onChange={handleNewSiteChange}
                                autoComplete="new-password"
                                size="small"
                                fullWidth
                                sx={{ bgcolor: theme.palette.background.paper, label: { color: "#aaa" } }}
                            />
                            <TextField
                                select
                                label="Country"
                                name="country"
                                value={newSite.country}
                                onChange={handleNewSiteChange}
                                size="small"
                                fullWidth
                                sx={{ bgcolor: theme.palette.background.paper, label: { color: "#aaa" } }}
                            >
                                {countriesList.map((country) => (
                                    <MenuItem
                                        key={country.code}
                                        value={country.code}
                                        sx={{ color: "#fff", bgcolor: theme.palette.background.paper }}
                                    >
                                        {country.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                        <Box sx={{ mt: "auto", p: 16, display: "flex", gap: 8, borderTop: "1px solid #333" }}>
                            <Button onClick={handleCloseCreateSite}>Cancel</Button>
                            <Button
                                variant="contained"
                                onClick={handleConfirmCreateSite}
                                disabled={!newSite.siteName || !newSite.country || !selectedTrial}
                            >
                                Create site
                            </Button>
                        </Box>
                    </Box>
                </Slide>
            </Box>
        </Dialog>
    );
};

export default AssignSitesModal;
